/**
 * Sound for pre-readers: spoken numbers/feedback via the Web Speech API, and
 * gentle musical cues synthesized with the Web Audio API (no audio files
 * needed). Everything is warm and positive — there is no "you lost" sound.
 *
 * Browsers block audio until the first user gesture, so call `unlock()` on the
 * very first tap.
 */

const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];

class GameAudio {
  private ctx: AudioContext | null = null;
  private unlocked = false;
  private muted = false;
  /** Master gain keeps everything at a child-friendly, gentle volume. */
  private master: GainNode | null = null;
  private voice: SpeechSynthesisVoice | null = null;

  /** Resume the audio context + prime speech. Safe to call repeatedly. */
  unlock(): void {
    if (this.unlocked) {
      void this.ctx?.resume();
      return;
    }
    try {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctx) {
        this.ctx = new Ctx();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.5;
        this.master.connect(this.ctx.destination);
        void this.ctx.resume();
      }
    } catch {
      /* audio simply stays silent if unavailable */
    }
    this.pickVoice();
    this.unlocked = true;
  }

  private pickVoice(): void {
    if (!('speechSynthesis' in window)) return;
    const choose = () => {
      const voices = window.speechSynthesis.getVoices();
      if (!voices.length) return;
      // Prefer an English voice; a clearer/female voice tends to suit kids.
      this.voice =
        voices.find((v) => /en[-_]?(US|GB)/i.test(v.lang) && /female|samantha|karen|zira/i.test(v.name)) ??
        voices.find((v) => /^en/i.test(v.lang)) ??
        voices[0];
    };
    choose();
    window.speechSynthesis.onvoiceschanged = choose;
  }

  /** Toggle all sound on/off (the speaker button). Returns the new muted state. */
  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.muted) this.stopSpeech();
    return this.muted;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  // ── Speech ────────────────────────────────────────────────────────────────
  speak(text: string, opts: { rate?: number; pitch?: number } = {}): void {
    if (this.muted || !('speechSynthesis' in window)) return;
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = opts.rate ?? 0.92;
      u.pitch = opts.pitch ?? 1.15;
      u.volume = 1;
      if (this.voice) u.voice = this.voice;
      window.speechSynthesis.speak(u);
    } catch {
      /* ignore */
    }
  }

  /** Cancel any queued speech (e.g. when starting a new round). */
  stopSpeech(): void {
    if ('speechSynthesis' in window) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }
  }

  speakNumber(n: number): void {
    const word = NUMBER_WORDS[n] ?? String(n);
    this.speak(word, { rate: 0.85, pitch: 1.2 });
  }

  // ── Synthesized cues ────────────────────────────────────────────────────────
  private tone(
    freq: number,
    start: number,
    duration: number,
    type: OscillatorType = 'sine',
    gain = 0.25,
  ): void {
    if (this.muted || !this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime + start;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    // Quick attack, smooth decay => soft, bell-like, never harsh.
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t0);
    osc.stop(t0 + duration + 0.02);
  }

  /** Soft pop when a scoop lands. */
  pop(): void {
    this.tone(420, 0, 0.12, 'triangle', 0.22);
    this.tone(640, 0.02, 0.1, 'sine', 0.14);
  }

  /** Bright coin ding. */
  coin(): void {
    this.tone(1180, 0, 0.12, 'sine', 0.2);
    this.tone(1560, 0.05, 0.14, 'sine', 0.16);
  }

  /** Happy success arpeggio (C–E–G–C). */
  chimeHappy(): void {
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((f, i) => this.tone(f, i * 0.09, 0.32, 'sine', 0.26));
  }

  /** Gentle, encouraging "try again" — a soft low-high nudge, not a buzzer. */
  wobble(): void {
    this.tone(330, 0, 0.18, 'sine', 0.22);
    this.tone(392, 0.14, 0.22, 'sine', 0.2);
  }

  /** Bigger celebration for leveling up or filling the jar. */
  fanfare(): void {
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5];
    notes.forEach((f, i) => this.tone(f, i * 0.1, 0.4, 'triangle', 0.24));
  }
}

export const Audio = new GameAudio();
